import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';

export type BinanceWithdrawalSource =
  | 'binance_withdrawal'
  | 'transaccion_cripto'
  | 'beneficiario';

export interface BinanceWithdrawalLocalUser {
  userId: string;
  userName: string;
  userEmail: string;
  localWithdrawalId: string | null;
  rail: string;
  walletMatches: boolean;
  source: BinanceWithdrawalSource;
}

export interface BinanceWithdrawalRow {
  binanceId: string;
  txId: string | null;
  coin: string;
  network: string;
  address: string;
  amount: number;
  transactionFee: number;
  status: number;
  statusText: string;
  applyTime: string | null;
  completeTime: string | null;
  localUser: BinanceWithdrawalLocalUser | null;
}

export interface BinanceWithdrawalsSearchResponse {
  data: BinanceWithdrawalRow[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    rangeStart: string;
    rangeEnd: string;
    walletAddress: string | null;
  };
}

export interface BinanceWithdrawalsSearchQuery {
  walletAddress?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class BinanceWithdrawalsSearchService {
  private readonly api = `${environment.apiNest}binance-withdrawals`;

  constructor(
    private http: HttpClient,
    private cognito: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognito.getSession();
    if (!session?.idToken?.jwtToken) {
      throw new Error('Sesión inválida. Iniciá sesión nuevamente.');
    }
    const token = session.idToken.jwtToken.trim();
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token}`);
  }

  private buildParams(q: BinanceWithdrawalsSearchQuery): HttpParams {
    let params = new HttpParams();
    if (q.walletAddress) params = params.set('walletAddress', q.walletAddress);
    if (q.startDate) params = params.set('startDate', q.startDate);
    if (q.endDate) params = params.set('endDate', q.endDate);
    if (q.page) params = params.set('page', String(q.page));
    if (q.limit) params = params.set('limit', String(q.limit));
    return params;
  }

  private handleError(error: HttpErrorResponse) {
    const msg =
      error.error?.message ||
      (typeof error.error === 'string' ? error.error : '') ||
      error.message ||
      'Error al consultar Binance';
    return throwError(() => ({
      statusCode: error.status,
      message: msg,
      raw: error.error,
    }));
  }

  search(
    q: BinanceWithdrawalsSearchQuery,
  ): Observable<BinanceWithdrawalsSearchResponse> {
    const url = `${this.api}/search-by-address`;
    const params = this.buildParams(q);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<BinanceWithdrawalsSearchResponse>(url, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  exportExcel(
    q: Omit<BinanceWithdrawalsSearchQuery, 'page' | 'limit'>,
  ): Observable<Blob> {
    const url = `${this.api}/search-by-address/export`;
    let params = new HttpParams();
    if (q.walletAddress) params = params.set('walletAddress', q.walletAddress);
    if (q.startDate) params = params.set('startDate', q.startDate);
    if (q.endDate) params = params.set('endDate', q.endDate);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get(url, {
          headers,
          params,
          responseType: 'blob',
        }),
      ),
      catchError(this.handleError),
    );
  }
}
